/* eslint-disable react-refresh/only-export-components */
import type { AsyncStatus } from "@react-hookz/web";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";

import { NO_CONTENT_TEXT } from "../../../config/insights.config";
import { navigationStore } from "../../../store/navigation";
import {
    isLoading,
    isSuccess,
    parseSharableLinkId,
} from "../../../utils/api.utils";
import { ContentUnavailable } from "../../ContentUnavailable";

import { InfoPanel } from "./InfoPanel";
import { InsightsLoaderSkeleton } from "./InsightsLoaderSkeleton";

export const StyledInsightContentGridRenderer = styled.div<{
    maxResults: number;
    noMarginTop: boolean;
    hidden: boolean;
}>`
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: ${({ maxResults }) =>
        `repeat(${maxResults}, minmax(0, 1fr))`};
    margin-bottom: 1px;
    margin-top: ${({ noMarginTop }) => (noMarginTop ? "0" : "1rem")};
    visibility: ${({ hidden }) => (hidden ? "hidden" : undefined)};
`;

export const StyledBackground = styled.div`
    background-color: ${({ theme }) => theme.surface.subtleOffset};
    margin-left: -24px;
    margin-right: -24px;
    padding: 0px 36px;
    min-height: 800px;
`;

export const MAX_GRID_RESULTS_PER_ROW = 4;

export interface RendererProps {
    isLoaderInView: boolean;
    setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
    setTotalCount: Dispatch<SetStateAction<number>>;
}

export function withInsightsRenderer<P>(
    InputComponent: React.FunctionComponent<P & RendererProps>,
    optionalParams: { [key: string]: any } = {},
): React.FunctionComponent<P> {
    const RendererComponentWithLoader = (props: P) => {
        const rawIdentifier = navigationStore.getState().identifier;
        const marketingContentIdValue = rawIdentifier
            ? parseSharableLinkId(rawIdentifier).identifier
            : undefined;

        const enableSingleIdentifierView = marketingContentIdValue;

        const [asyncStatus, setAsyncStatus] =
            useState<AsyncStatus>("not-executed");
        const [totalCount, setTotalCount] = useState<number>(0);
        const { ref: loaderRef, inView } = useInView();
        const { showSearchSortDropdown } = optionalParams;
        return (
            <StyledBackground data-testid="insights-renderer">
                <InfoPanel
                    insightCount={totalCount}
                    showSearchSortDropdown={
                        showSearchSortDropdown && !enableSingleIdentifierView
                    }
                />
                <StyledInsightContentGridRenderer
                    maxResults={MAX_GRID_RESULTS_PER_ROW}
                    noMarginTop={true}
                    hidden={false}
                >
                    <InputComponent
                        {...props}
                        isLoaderInView={inView}
                        setAsyncStatus={setAsyncStatus}
                        setTotalCount={setTotalCount}
                    />
                </StyledInsightContentGridRenderer>
                <StyledInsightContentGridRenderer
                    maxResults={MAX_GRID_RESULTS_PER_ROW}
                    noMarginTop={false}
                    ref={loaderRef}
                    hidden={!isLoading(asyncStatus)}
                >
                    {inView && isLoading(asyncStatus) && (
                        <InsightsLoaderSkeleton />
                    )}
                </StyledInsightContentGridRenderer>
                {isSuccess(asyncStatus) && totalCount === 0 && (
                    <ContentUnavailable message={NO_CONTENT_TEXT} />
                )}
            </StyledBackground>
        );
    };
    return RendererComponentWithLoader;
}
